import * as React from 'react';
import { Dropdown, IDropdownOption, IComboBoxOption, SearchBox } from '@fluentui/react';
import { POOL_ALL_KEY, toggleAllMulti, withAllFirst } from '../../../home/helpers/PoolSelectionHelper';


type Props = {
  poolOptions: IComboBoxOption[];
  value: (string | number)[];
  onChange: (next: (string | number)[]) => void;
  disabled?: boolean;
  label?: string;
  enableSearch?: boolean;
  'data-testid'?: string;
  placeholder?: string;
};

export default function SelectAndSearchDropdown({
  poolOptions,
  value,
  onChange,
  disabled,
  label = 'Pools',
  enableSearch = false,
  placeholder,
  ...rest
}: Props) {

  // full options including ALL
  const options = React.useMemo(() => withAllFirst(poolOptions), [poolOptions]);

  const [searchText, setSearchText] = React.useState('');

  // filtered items for display only
  const filteredOptions = React.useMemo(() => {
    if (!enableSearch || !searchText) return options;
    const t = searchText.toLowerCase();
    return options.filter(o => o.text.toLowerCase().includes(t));
  }, [searchText, enableSearch, options]);

  const handleChange = (_e: any, option?: IDropdownOption) => {
  if (!option) return;

  const next = toggleAllMulti(option as any, uiSelectedKeys, options);

  const clickedAll = option.key === POOL_ALL_KEY;

  if (clickedAll) {
    const allNowSelected = next.includes(POOL_ALL_KEY);

    if (allNowSelected) {
      onChange(['All']);
      return;
    } else {
      onChange([]);
      return;
    }
  }

  const withoutAll = next.filter(k => k !== POOL_ALL_KEY);

  const nowAllSelected = allKeys.every(k => withoutAll.includes(k));

  if (nowAllSelected) {
    onChange(['All']);
  } else {
    onChange(withoutAll);
  }
};


const allKeys = React.useMemo(
  () => options.filter(o => o.key !== POOL_ALL_KEY).map(o => o.key),
  [options]
);

const uiSelectedKeys = React.useMemo(() => {
  if (value.includes('All')) {
    return [POOL_ALL_KEY, ...allKeys];
  }

  return value; // normal selection
}, [value, allKeys]);

  return (
    <Dropdown
      {...rest}
      label={label}
      options={filteredOptions}
      selectedKeys={uiSelectedKeys  as any}
      disabled={!!disabled}
      multiSelect
      onChange={handleChange}
      data-is-focusable
      calloutProps={{ calloutMaxHeight: 270 }}

      onRenderList={(props, defaultRender) => (
        <div>
          {enableSearch && (
            <div style={{ padding: 4 }}>
              <SearchBox
                placeholder={placeholder || 'Search...'}
                value={searchText}
                onChange={(_, v) => setSearchText(v ?? '')}
              />
            </div>
          )}
          {defaultRender?.(props)}
        </div>
      )}

      onRenderTitle={(items) => {
        if (!items || items.length === 0) return null;
        const display = items
          .filter(i => String(i.key) !== String(POOL_ALL_KEY))
          .map(i => i.text)
          .join(', ');
        return <span>{display}</span>;
      }}
    />
  );
}
